import { apiUrl } from '../utils/appOrigin';
import { getSupabase } from './supabase/client';

/**
 * @returns {Promise<string | null>}
 */
async function getAccessToken() {
  const client = getSupabase();
  if (!client) return null;
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Refresh session when access token may be stale. */
async function getFreshAccessToken() {
  const client = getSupabase();
  if (!client) return null;
  const { data: current } = await client.auth.getSession();
  if (current.session?.access_token) return current.session.access_token;
  const { data: refreshed, error } = await client.auth.refreshSession();
  if (error) return null;
  return refreshed.session?.access_token ?? null;
}

/**
 * @param {string} path
 * @param {RequestInit & { auth?: boolean | 'optional' }} opts
 */
async function bhriguFetch(path, opts = {}) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  const authMode = opts.auth === undefined ? true : opts.auth;

  if (authMode === 'optional') {
    const token = await getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } else if (authMode !== false) {
    const token = await getFreshAccessToken();
    if (!token) throw new Error('Must be signed in');
    headers.Authorization = `Bearer ${token}`;
  }

  let res;
  try {
    res = await fetch(apiUrl(path), {
      ...opts,
      headers,
      body: opts.body != null ? JSON.stringify(opts.body) : undefined,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Network error';
    throw new Error(msg.includes('fetch') ? 'Could not reach the server. Is the API running?' : msg);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const fallback =
      data.code === 'invalid_question'
        ? data.messageEn || data.error || `Request failed (${res.status})`
        : data.error || `Request failed (${res.status})`;
    const err = new Error(fallback);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/** @param {string} query @param {object} [opts] */
export async function searchBhriguContent(query, opts = {}) {
  const data = await bhriguFetch('/api/bhrigu/search', {
    method: 'POST',
    auth: 'optional',
    body: {
      query,
      limit: opts.limit ?? 5,
      lessonLimit: opts.lessonLimit ?? 6,
      showEnglish: opts.showEnglish !== false,
      pageKey: opts.pageKey ?? undefined,
      pageTitle: opts.pageTitle ?? undefined,
      boostTopicIds: opts.boostTopicIds ?? [],
      section: opts.section ?? 'jyotisha',
      school: opts.school ?? 'parashari',
    },
  });
  return data;
}

/** @deprecated use searchBhriguContent */
export async function searchSimilarQuestions(query, limit = 5) {
  const data = await searchBhriguContent(query, { limit });
  return data.similar || [];
}

/** @param {object} payload */
export async function logBhriguCacheHit(payload) {
  return bhriguFetch('/api/bhrigu/cache-hit', { method: 'POST', body: payload });
}

export async function fetchBhriguQuota() {
  const data = await bhriguFetch('/api/bhrigu/quota', { method: 'GET' });
  return data.quota;
}

/** @param {{ limit?: number }} [opts] */
export async function fetchBhriguRecentFeed(opts = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const data = await bhriguFetch(`/api/bhrigu/recent${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    auth: 'optional',
  });
  return data.recent || [];
}

/** @param {{ limit?: number; offset?: number }} [opts] */
export async function fetchBhriguHistory(opts = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const data = await bhriguFetch(`/api/bhrigu/history${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return data.history || [];
}

/**
 * @param {object} payload
 * @param {'global' | 'page_chat'} surface
 */
export async function askBhriguAi(payload, surface = 'global') {
  const path = surface === 'page_chat' ? '/api/bhrigu/page-chat' : '/api/bhrigu/ask';
  return bhriguFetch(path, { method: 'POST', body: payload });
}

/** @param {object} payload */
export async function analyzeSyllabusChart(payload) {
  return bhriguFetch('/api/bhrigu/chart-learn', { method: 'POST', body: payload });
}

export async function fetchSyllabusChartSession() {
  const data = await bhriguFetch('/api/bhrigu/chart-session', { method: 'GET' });
  return data.session ?? null;
}

/** @param {object} payload */
export async function saveSyllabusChartSession(payload) {
  const data = await bhriguFetch('/api/bhrigu/chart-session', { method: 'PUT', body: payload });
  return data.session ?? null;
}

export async function clearSyllabusChartSession() {
  return bhriguFetch('/api/bhrigu/chart-session', { method: 'DELETE' });
}
